import { api } from './client';

export type Direction = 'IN' | 'OUT';
export type MatchConfidence = 'EDRPOU' | 'IBAN' | 'RULE' | 'NONE';
// ч.1231: формат підтягується за рахунком (`BankAccount.statement_formats`); `auto` — сервер
// визначить сам. «Канонічний CSV» з інтерфейсу прибрано (сервер приймає за явною назвою).
export type StatementFormat = 'auto' | 'canonical' | 'privatbank' | 'pumb' | 'monobank' | 'ukrsib' | 'raiffeisen' | 'mtb' | 'otp' | 'oschad' | '1c';

export interface StatementRow {
  line_number: number;
  date: string;
  direction: Direction;
  amount: string;
  counterparty_name: string | null;
  counterparty_edrpou: string | null;
  counterparty_iban: string | null;
  purpose: string | null;
  doc_number: string | null;
  doc_date: string | null;
  matched_contractor_id: string | null;
  matched_contractor_name: string | null;
  // ч.810: працівник (фізособа) — виплата ЗП без контрагента.
  matched_employee_id?: string | null;
  matched_individual_id?: string | null;
  matched_employee_name?: string | null;
  match_confidence: MatchConfidence;
  target_operation_type: string | null;
  review_doc_id?: string | null;  // ч.833
  duplicate_of_id: string | null;
  duplicate_of_kind: 'receipt' | 'payment' | null;
  duplicate_of_number: string | null;
  parse_error: string | null;
  // ч.725: розумний розбір.
  matched_contract_id: string | null;
  matched_contract_name: string | null;
  matched_order_id: string | null;
  matched_order_kind: 'customer' | 'supplier' | null;
  matched_order_number: string | null;
  matched_payment_order_id: string | null;
  matched_payment_order_number: string | null;
  // ч.727: наша чернетка списання (/bank-payments «збережені»), яку виписка підтверджує.
  matched_bank_payment_id?: string | null;
  matched_bank_payment_number?: string | null;
  rule_id: string | null;
  suggested_cash_flow_item_id: string | null;
  recognized: boolean;
  // ч.810: FULL / PARTIAL (є контрагент, бракує виду операції або ДДС) / NONE.
  recognition?: 'FULL' | 'PARTIAL' | 'NONE';
  review_reasons?: string[];
  bank_ref?: string | null;         // ч.795: референс операції банку (API)
}

export interface StatementPreview {
  organization_id: string;
  bank_account_id: string;
  rows: StatementRow[];
  total_in: string;
  total_out: string;
  matched_count: number;
  unmatched_count: number;
  duplicate_count: number;
  recognized_count: number;
  review_count: number;
  /** ч.1231: виписка з підключення банку — його «Автопроводки розпізнаних»; false → основна
   *  дія сторінки створює чернетки. null / немає — джерело не підключення (файл). */
  auto_post_recognized?: boolean | null;
}

export interface ImportRowApply {
  line_number: number;
  skip: boolean;
  allow_duplicate?: boolean;
  direction: Direction;
  date: string;
  amount: string;
  contractor_id?: string | null;
  employee_id?: string | null;   // ч.810: SALARY — працівник
  contract_id?: string | null;
  operation_type: string;
  purpose?: string | null;
  document_number?: string | null;
  incoming_doc_number?: string | null;
  incoming_doc_date?: string | null;
  payer_name?: string | null;
  payer_edrpou?: string | null;
  recipient_name?: string | null;
  recipient_edrpou?: string | null;
  // ч.725
  cash_flow_item_id?: string | null;
  customer_order_id?: string | null;
  supplier_order_id?: string | null;
  payment_order_id?: string | null;
  bank_payment_id?: string | null;
  review_doc_id?: string | null;  // ч.833: оновити існуючий нерозпізнаний документ
  basis_doc_id?: string | null;      // ч.790: «На підставі» — накладна (реалізація / надходження)
  counterparty_edrpou?: string | null;
  counterparty_iban?: string | null;
  remember_rule?: boolean;
  post?: boolean;
  bank_ref?: string | null;      // ч.795: з превʼю API
  needs_review?: boolean;        // ч.795: нерозпізнане → чернетка з ⚠
  ai_suggestion?: Record<string, string | null> | null;  // ч.821: памʼять Oblik+/AI
}

export interface BankMatchRule {
  id: string;
  organization_id: string;
  direction: Direction;
  counterparty_edrpou: string | null;
  counterparty_iban: string | null;
  purpose_pattern: string | null;
  contractor_id: string | null;
  contractor_name: string | null;
  contract_id: string | null;
  contract_name: string | null;
  cash_flow_item_id: string | null;
  operation_type: string | null;
  hit_count: number;
  last_used_at: string | null;
}

export interface ImportApplyResult {
  created_receipts: number;
  created_payments: number;
  skipped: number;
  skipped_as_duplicate: number;
  errors: string[];
  receipt_ids: string[];
  payment_ids: string[];
  posted: number;
  rules_saved: number;
  payment_orders_executed: number;
  bank_payments_confirmed?: number;
  reviewed?: number;  // ч.833
  done_lines?: number[];       // ч.931: рядки, по яких документ є
  duplicate_lines?: number[];  // ч.931: пропущені сервером як дублі
}

/** ч.819: пропозиція помічника Oblik+/AI по рядку виписки — лише підказка. */
export interface AssistSuggestion {
  line_number: number;
  contractor_id: string | null;
  contractor_new_name: string | null;
  employee_id: string | null;
  operation_type: string;
  cash_flow_item_id: string | null;
  confidence: number;
  reason: string;
}
export interface AssistResult {
  suggestions: AssistSuggestion[];
  model: string;
  input_tokens: number;
  output_tokens: number;
  charged_credits: number;
  balance_after: number | null;
  /** ч.1097: рядків, які довелось перепитати точнішим розбором. */
  escalated_rows?: number;
}

export const bankImport = {
  assist: (organizationId: string, rows: Array<Pick<ImportRowApply,
    'line_number' | 'direction' | 'date' | 'amount' | 'purpose' | 'contractor_id' | 'employee_id'
    | 'operation_type' | 'cash_flow_item_id' | 'counterparty_edrpou' | 'counterparty_iban'> & { counterparty_name: string | null }>) =>
    api.post<AssistResult>('/bank-import/assist', { organization_id: organizationId, rows }).then((r) => r.data),
  preview: async (
    organizationId: string,
    bankAccountId: string,
    file: File,
    format: StatementFormat = 'auto',
  ): Promise<StatementPreview> => {
    const fd = new FormData();
    fd.append('organization_id', organizationId);
    fd.append('bank_account_id', bankAccountId);
    fd.append('file', file);
    fd.append('format', format);
    const { data } = await api.post<StatementPreview>('/bank-import/preview', fd);
    return data;
  },
  /** ч.795: виписка з API банку → той самий розбір (StatementPreview), що файл. */
  previewApi: async (
    organizationId: string, bankAccountId: string, dateFrom?: string, dateTo?: string,
  ): Promise<StatementPreview> => {
    const { data } = await api.post<StatementPreview>('/bank-import/preview-api', {
      organization_id: organizationId, bank_account_id: bankAccountId,
      date_from: dateFrom || null, date_to: dateTo || null,
    });
    return data;
  },
  /** ч.833: «нерозпізнані» документи → рядки як після розбору виписки. */
  review: async (organizationId: string, direction: Direction, bankAccountId?: string): Promise<StatementPreview> => {
    const { data } = await api.post<StatementPreview>('/bank-import/review', {
      organization_id: organizationId, direction, bank_account_id: bankAccountId || null,
    });
    return data;
  },
  apply: async (
    organizationId: string,
    bankAccountId: string | null,
    rows: ImportRowApply[],
  ): Promise<ImportApplyResult> => {
    const { data } = await api.post<ImportApplyResult>('/bank-import/apply', {
      organization_id: organizationId,
      bank_account_id: bankAccountId,
      rows,
    });
    return data;
  },
  // ч.832: серверна пагінація + фільтр платник/отримувач + сортування.
  rules: (organizationId: string, params?: {
    q?: string; direction?: Direction | ''; limit?: number; offset?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
  }) =>
    api.get<{ items: BankMatchRule[]; total: number; limit: number; offset: number }>('/bank-import/rules', {
      params: { organization_id: organizationId, ...params, direction: params?.direction || undefined,
        q: params?.q || undefined },
    }).then((r) => r.data),
  deleteRule: (id: string) => api.delete(`/bank-import/rules/${id}`).then(() => undefined),
};
